import React, { MouseEvent } from 'react'
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride'
import { useMount } from 'react-use'
import { useLocation } from 'react-router-dom'

import { useAppSelector } from 'app/store'
import { HAS_SEEN_TUTORIAL_ON_THE_FIRST_LOGIN } from 'app/constants/client-state.constants'
import { useProgramContext } from 'app/contexts/program/program.context'
import routesConstants from 'app/constants/routes.constants'
import {
  getClientStateData,
  setClientStateData,
} from 'app/services/api/client-state.service'
import useReadOnly from 'app/hooks/use-read-only.hook'

import TutorialTooltip from '../TutorialTooltip/TutorialTooltip'
import useTutorialSettings from '../TutorialSettingsProvider/useTutorialSettings'

export type TutorialElementProps = {
  'aria-label': string
  'data-action': string
  'onClick': (event: MouseEvent<HTMLButtonElement>) => void
  'role': string
  'title': string
}

const Tutorial = () => {
  const { isTeacher } = useAppSelector((state) => state.userData.kcInfo)
  const { tutorialSettings, setTutorialSettings, handleStop, handlePlay } =
    useTutorialSettings()
  const { filterValue } = useProgramContext()
  const location = useLocation()

  const isReadOnly = useReadOnly()

  const shouldTutorialBeShown = () => {
    const isUserOnHomePage = location.pathname === routesConstants.HOME
    return isUserOnHomePage && isTeacher && !isReadOnly
  }

  useMount(() => {
    if (isReadOnly) return

    const checkIfUserHasSeenTutorialOnFirstLogin = async () => {
      const clientData = await getClientStateData(
        HAS_SEEN_TUTORIAL_ON_THE_FIRST_LOGIN
      )
      if (!clientData.hasSeenTutorialOnTheFirstLogin) {
        await setClientStateData(HAS_SEEN_TUTORIAL_ON_THE_FIRST_LOGIN, {
          hasSeenTutorialOnTheFirstLogin: true,
        })
        handlePlay()
      }
    }
    checkIfUserHasSeenTutorialOnFirstLogin().catch((error) => {
      console.error(error)
    })
  })

  const handleJoyrideCallback = ({
    action,
    index,
    status,
    type,
  }: CallBackProps) => {
    const { CLOSE, PREV } = ACTIONS
    const { STEP_AFTER, TARGET_NOT_FOUND } = EVENTS
    const { FINISHED, SKIPPED } = STATUS
    if (action === CLOSE) {
      handleStop()
      return
    }

    if (type === STEP_AFTER || type === TARGET_NOT_FOUND) {
      setTutorialSettings((prevSettings) => ({
        ...prevSettings,
        stepIndex: index + (action === PREV ? -1 : 1),
      }))
      return
    }
    if (status === FINISHED || status === SKIPPED) {
      handleStop()
    }
  }

  if (!shouldTutorialBeShown()) return null

  return (
    <Joyride
      run={tutorialSettings.run}
      steps={tutorialSettings.steps[filterValue].steps}
      continuous
      callback={handleJoyrideCallback}
      tooltipComponent={TutorialTooltip}
      stepIndex={tutorialSettings.stepIndex}
    />
  )
}

export default Tutorial
